import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/* Locks body scroll and wires Escape/arrow-key navigation while the lightbox is open */
function useLightboxKeys({ open, count, onClose, setIdx }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % count);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + count) % count);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, count, onClose, setIdx]);
}

const NavArrows = ({ onPrev, onNext }) => (
  <>
    <button
      type="button"
      onClick={onPrev}
      aria-label="Anterior"
      data-testid="lightbox-prev"
      className="absolute left-3 sm:left-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
    >
      <ChevronLeft size={22} />
    </button>
    <button
      type="button"
      onClick={onNext}
      aria-label="Siguiente"
      data-testid="lightbox-next"
      className="absolute right-3 sm:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
    >
      <ChevronRight size={22} />
    </button>
  </>
);

const Dots = ({ count, idx, onSelect }) => (
  <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
    {Array.from({ length: count }).map((_, i) => (
      <button
        key={i}
        type="button"
        onClick={(e) => { e.stopPropagation(); onSelect(i); }}
        aria-label={`Ir a imagen ${i + 1}`}
        data-testid={`lightbox-dot-${i}`}
        className={`h-1.5 rounded-full transition-all ${i === idx ? "w-7 bg-white" : "w-2 bg-white/40 hover:bg-white/70"}`}
      />
    ))}
  </div>
);

/**
 * Fullscreen image lightbox.
 * Props:
 *  - images: array of URLs
 *  - startIndex: initial image index
 *  - open: boolean
 *  - onClose: () => void
 */
export default function Lightbox({ images = [], startIndex = 0, open, onClose }) {
  const [idx, setIdx] = React.useState(startIndex);

  useEffect(() => {
    if (open) setIdx(startIndex);
  }, [open, startIndex]);

  useLightboxKeys({ open, count: images.length, onClose, setIdx });

  if (!images.length) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            aria-label="Cerrar"
            data-testid="lightbox-close"
            className="absolute top-4 right-4 z-20 w-12 h-12 rounded-full bg-white text-black hover:bg-[var(--faro-yellow)] flex items-center justify-center transition-colors shadow-lg"
          >
            <X size={24} strokeWidth={2.5} />
          </button>

          {images.length > 1 && (
            <NavArrows
              onPrev={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }}
              onNext={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }}
            />
          )}

          <AnimatePresence mode="wait">
            <motion.img
              key={images[idx]}
              src={images[idx]}
              alt={`Imagen ${idx + 1}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={onClose}
              data-testid="lightbox-image"
              className="max-w-[95vw] max-h-[92vh] object-contain rounded-lg shadow-2xl cursor-zoom-out"
            />
          </AnimatePresence>

          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-widest font-semibold pointer-events-none">
            Haz clic para cerrar
          </div>

          {images.length > 1 && <Dots count={images.length} idx={idx} onSelect={setIdx} />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
